import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Card, CardContent, FormControl, Grid, InputLabel, Paper, Select,
    Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { Search as IconSearch, ArrowBack as IconVolver } from "@mui/icons-material";
import { Link } from "react-router-dom";

import api from "../../services/api";
import { getsListNaturalPersonByTypeAndName } from "../../services/personaService";
import FiltroFilial from "../../components/common/FiltroFilial";
import { useFiltroFilial } from "../../hooks/useFiltroFilial";

// Horarios disponibles de un terapeuta.
//
// Los bloques ocupados salen de terapiahorario de las terapias activas del
// terapeuta: la hora de inicio es la del bloque y la duracion viene de la tarifa
// de la terapia (conceptoCobro.MinutosSesion), que es lo que da la hora de fin.
//
// Cada celda es una barra vertical con los 60 minutos de la hora, de arriba
// abajo: rosado el minuto ocupado, verde el libre. La celda lleva encima los
// minutos ocupados de esa hora. Asi, mirando la columna de un dia de la semana,
// se ve la franja continua que ocupan las sesiones y donde quedan huecos reales
// para agendar. La cabecera de cada columna totaliza el tiempo libre del dia.
// Las filas llegan hasta la hora de fin de atencion de la configuracion. La
// ventana es solo de consulta: no guarda nada.

// Dias de lunes a sabado; el domingo no se atiende.
const DIAS_SEMANA = ["Lunes", "Martes", "Miercoles", "Jueves", "Viernes", "Sabado"];

// Dia que usa el rango del sabado.
const DIA_SABADO = "Sabado";

// Turnos del dia. El corte se toma de la hora de inicio de la tarde, que es la
// misma que cierra el rango del sabado.
const TURNOS = [
    { id: 0, descripcion: "(Todos)" },
    { id: 1, descripcion: "Mañana" },
    { id: 2, descripcion: "Tarde" },
];

// Valores por defecto si la tabla configuracion no tiene los parametros.
const HORARIO_POR_DEFECTO = {
    LV_inicio: "08:00",
    LV_fin: "19:00",
    SA_inicio: "08:00",
    SA_fin: "13:00",
};

const Index = () => {
    const [terapeutas, setTerapeutas] = useState([]);
    const [terapeutaSeleccionado, setTerapeutaSeleccionado] = useState(0);
    const [turno, setTurno] = useState(0);
    // La filial del usuario: si tiene una, el filtro queda en esa y bloqueado.
    // Los terapeutas del desplegable son los que tienen terapias activas ahi.
    const { filiales, idFilial, setIdFilial, bloqueado, terapeutas: terapeutasFilial, terapeutasListos } = useFiltroFilial();

    const [config, setConfig] = useState(HORARIO_POR_DEFECTO);
    const [disponibilidad, setDisponibilidad] = useState([]);

    const [cargando, setCargando] = useState(false);
    const [filtrado, setFiltrado] = useState(false);

    const [openMessage, setOpenMessage] = useState(false);
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });

    const avisar = (severity, message) => {
        setPropsMessage({ severity, message });
        setOpenMessage(true);
    }

    const fetchTerapeutas = useCallback(async () => {
        const lista = await getsListNaturalPersonByTypeAndName(25, "", true)
            .catch((err) => { console.log("Err: ", err); return []; });

        setTerapeutas(lista || []);
    }, []);

    const fetchConfig = useCallback(async () => {
        const response = await api.get("/Seguridad/GetParametros")
            .catch((err) => { console.log("Err: ", err); return null; });

        const params = (response && response.data && response.data.data) || {};

        setConfig({
            LV_inicio: params.HORARIO_ATENCION_INICIO_LV || HORARIO_POR_DEFECTO.LV_inicio,
            LV_fin: params.HORARIO_ATENCION_FIN_LV || HORARIO_POR_DEFECTO.LV_fin,
            SA_inicio: params.HORARIO_ATENCION_INICIO_SA || HORARIO_POR_DEFECTO.SA_inicio,
            SA_fin: params.HORARIO_ATENCION_FIN_SA || HORARIO_POR_DEFECTO.SA_fin,
        });
    }, []);

    useEffect(() => {
        fetchTerapeutas();
        fetchConfig();
    }, []);

    // Sin filial se listan todos los terapeutas. Con una filial, solo los que
    // tienen una terapia activa en esa sede.
    const opcionesTerapeuta = useMemo(() => {
        if (!(idFilial > 0)) return terapeutas;
        if (!terapeutasListos) return [];

        const porId = new Map(terapeutas.map((t) => [t.id, t]));

        return terapeutasFilial.map((t) => porId.get(t.id) || { id: t.id, descripcion: t.descripcion });
    }, [terapeutas, terapeutasFilial, idFilial, terapeutasListos]);

    // La tabla solo se arma cuando se elige terapeuta y se filtra.
    const filtrar = async () => {
        if (!terapeutaSeleccionado || terapeutaSeleccionado <= 0) {
            avisar("warning", "Elija un terapeuta.");
            return;
        }

        setCargando(true);

        const response = await api.get("/Terapia/GetsHorariosTerapeuta", { params: { idTerapeuta: terapeutaSeleccionado, idFilial: idFilial || 0 } })
            .catch((err) => { console.log("Err: ", err); return null; });

        const bloques = (response && response.data && response.data.data) || [];

        calcularDisponibilidad(bloques, config, turno);

        setFiltrado(true);
        setCargando(false);
    };

    // Cada celda es una barra vertical con los 60 minutos de la hora, uno tras
    // otro de arriba abajo. Cada minuto se pinta de rosado si esta ocupado y de
    // verde si esta libre, de modo que al mirar una columna verticalmente se ve
    // la franja real ocupada por las sesiones.
    //
    // Ademas se guardan los bloques de terapia que tocan cada celda, que son los
    // que se listan en el tooltip con su hora de inicio y de fin.
    const calcularDisponibilidad = (bloques, configActual, turnoElegido) => {
        const rangos = {
            LV: { inicio: configActual.LV_inicio, fin: configActual.LV_fin },
            SA: { inicio: configActual.SA_inicio, fin: configActual.SA_fin },
        };

        const aMinutos = (hora) => {
            const partes = (hora || "00:00").split(":");
            return parseInt(partes[0], 10) * 60 + parseInt(partes[1] || 0, 10);
        };

        // Minutos ocupados de cada celda. Un Set no cuenta dos veces el mismo
        // minuto, asi que dos bloques sobre la misma hora no la dejan en cero.
        const ocupadosPorCelda = {};

        for (const dia of DIAS_SEMANA) {
            const rango = rangos[dia === DIA_SABADO ? "SA" : "LV"];

            const desde = aMinutos(rango.inicio);
            const hasta = aMinutos(rango.fin);

            // La hora de fin tambien se muestra, aunque ningun minuto suyo cae
            // dentro del rango.
            for (let minuto = desde; minuto <= hasta; minuto++) {
                ocupadosPorCelda[`${dia}|${Math.floor(minuto / 60)}`] = new Set();
            }
        }

        // Bloques que tocan cada celda, para el tooltip.
        const bloquesPorCelda = {};

        for (const bloque of bloques || []) {
            const dia = normalizarDia(bloque.dia);
            const desde = aMinutos(bloque.horaInicio);
            const hasta = aMinutos(bloque.horaFin);

            for (let minuto = desde; minuto < hasta; minuto++) {
                const clave = `${dia}|${Math.floor(minuto / 60)}`;
                const celda = ocupadosPorCelda[clave];

                if (!celda) continue;

                celda.add(minuto);

                if (!bloquesPorCelda[clave]) bloquesPorCelda[clave] = [];

                // Un bloque que ocupa 45 minutos cae en una sola hora, pero si
                // pasara de una hora a otra se listaria una vez en cada celda.
                if (!bloquesPorCelda[clave].includes(bloque)) {
                    bloquesPorCelda[clave].push(bloque);
                }
            }
        }

        const resultado = Object.keys(ocupadosPorCelda)
            .map((clave) => {
                const [dia, hora] = clave.split("|");

                const minutosOcupados = ocupadosPorCelda[clave];

                // Los 60 minutos de la hora, de arriba abajo. Cada uno lleva si
                // esta ocupado, y si ademas cae dentro del rango de atencion.
                const rango = rangos[dia === DIA_SABADO ? "SA" : "LV"];
                const desde = aMinutos(rango.inicio);
                const hasta = aMinutos(rango.fin);
                const horaNumero = parseInt(hora, 10);

                const minutos = [];

                for (let dentro = 0; dentro < 60; dentro++) {
                    const minuto = horaNumero * 60 + dentro;

                    minutos.push({
                        minuto: dentro,
                        ocupado: minutosOcupados.has(minuto),
                        enRango: minuto >= desde && minuto <= hasta,
                    });
                }

                const libres = minutos.filter((m) => m.enRango && !m.ocupado).length;
                const ocupados = minutos.filter((m) => m.enRango && m.ocupado).length;

                // Una hora esta dentro del horario de atencion cuando arranca antes de que
                // este cierre. La ultima fila (la hora de fin) ya no es hora de
                // atencion: de ella no queda ningun minuto por atender.
                const enHorario = horaNumero * 60 < hasta;

                return {
                    dia,
                    hora: horaNumero,
                    minutos,
                    libres,
                    ocupados,
                    enHorario,
                    bloques: bloquesPorCelda[clave] || [],
                };
            })
            .filter((celda) => enTurno(celda.hora, turnoElegido, rangos.SA.fin));

        setDisponibilidad(resultado);
    };

    // El total de un dia se descompone siempre en horas y minutos, aunque uno de los
    // dos sea cero: se ve igual de rapido que "0 h 0 min" que "11 h".
    const textoTotal = (minutos) => {
        const horas = Math.floor(minutos / 60);
        const resto = minutos % 60;

        return horas + " h " + resto + " min";
    };

    // Colores de la barra: verde el minuto libre y rojo el ocupado.
    const COLOR_OCUPADO = "#F4A6A6";
    const COLOR_LIBRE = "#B7E4C7";
    const COLOR_FUERA = "#fafafa";
    const COLOR_TEXTO = "#1a1a1a";

    // Linea blanca que separa las celdas entre si y de las columnas vecinas.
    const SEPARADOR = "1px solid #ffffff";

    // Alto de la barra de cada celda. Con 60 minutos son franjas de medio pixel,
    // que en conjunto forman la franja continua de la hora.
    const ALTO_BARRA = 28;

    // Minutos libres de la hora, en texto. Con la hora entera libre se muestra
    // "Libre"; si no, los minutos que quedan.
    const textoLibres = (libres) => (libres >= 60 ? "Libre" : String(libres) + " min");

    // Tooltip de la celda: las terapias que caen en esa hora y ese dia. De cada una
    // se ve su primer participante y la hora de inicio y de fin, sin la hora de la
    // fila ni los minutos de duracion.
    const detalleDe = (celda) => {
        if (!celda.enHorario) return "Fuera del horario de atención";

        if (celda.bloques.length === 0) return "Sin therapies en esta hora";

        return celda.bloques
            .map((b) => (b.participante ? b.participante + " - " : "")
                + b.horaInicio + " - " + b.horaFin)
            .join("\n");
    };

    // Total de tiempo libre del dia, para la cabecera de la columna. Antes de
    // filtrar no hay datos, asi que se muestra en cero.
    const libresDelDia = (dia) => {
        const total = disponibilidad
            .filter((celda) => celda.dia === dia)
            .reduce((suma, celda) => suma + celda.libres, 0);

        return textoTotal(total);
    };

    // Un bloque cae en el turno segun la hora en que arranca.
    const enTurno = (hora, turnoElegido, finSabado) => {
        if (!turnoElegido) return true;

        const corte = parseInt((finSabado || "13:00").split(":")[0], 10);

        return turnoElegido === 1 ? hora < corte : hora >= corte;
    };

    // Horas que se muestran en la tabla, segun el turno elegido.
    const horasVisibles = () => {
        const rango = { inicio: config.LV_inicio, fin: config.LV_fin };

        const desde = parseInt(rango.inicio.split(":")[0], 10);
        const hasta = parseInt(rango.fin.split(":")[0], 10);

        const horas = [];

        for (let hora = desde; hora <= hasta; hora++) {
            if (enTurno(hora, turno, config.SA_fin)) horas.push(hora);
        }

        return horas;
    };

    const celdaDe = (dia, hora) => {
        return disponibilidad.find((c) => c.dia === dia && c.hora === hora) || null;
    };

    // Barra vertical de una hora: una franja por minuto, de arriba abajo, con el
    // minuto ocupado en rosado y el libre en verde. Mirando la columna de un dia
    // se ve la franja continua que ocupan sus sesiones.
    //
    // Las franjas se pintan con un degradado en un solo div, en vez de 60 divs
    // apilados: asi no queda espacio entre ellas, que con tan poco alto se ve
    // como rayas en la celda.
    const BarraMinutos = ({ celda, hora }) => {
        // Se agrupan los minutos de corrido con el mismo color para no repetir
        // paradas: un degradado por tramo, de arriba abajo.
        const tramos = [];

        celda.minutos.forEach((minuto, indice) => {
            const color = !minuto.enRango
                ? COLOR_FUERA
                : (minuto.ocupado ? COLOR_OCUPADO : COLOR_LIBRE);

            const desde = (indice * 100) / 60;
            const hasta = ((indice + 1) * 100) / 60;

            const ultimo = tramos[tramos.length - 1];

            if (ultimo && ultimo.color === color) {
                ultimo.hasta = hasta;
                return;
            }

            tramos.push({ color, desde, hasta });
        });

        const degradado = tramos
            .flatMap((tramo) => [
                tramo.color + " " + tramo.desde + "%",
                tramo.color + " " + tramo.hasta + "%",
            ])
            .join(", ");

        return (
            <Tooltip title={detalleDe(celda, hora)} arrow>
                <Box sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    backgroundImage: "linear-gradient(to bottom, " + degradado + ")",
                }}>
                    {/* En las horas fuera del horario de atencion no se muestra
                        texto: la celda va en gris, sin nada escrito. */}
                    {celda.enHorario && (
                        <Typography
                            variant="caption"
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.8rem",
                                lineHeight: 1,
                                textTransform: "uppercase",
                                color: COLOR_TEXTO,
                            }}
                        >
                            {textoLibres(celda.libres)}
                        </Typography>
                    )}
                </Box>
            </Tooltip>
        );
    };

    return (
        <Box sx={{ p: 2 }}>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={12}>
                    {/* Esta pantalla se abre tambien desde la portada de
                        reportes: se puede volver a ella. */}
                    <Button component={Link} to="/reporte" startIcon={<IconVolver />} size="small"
                        sx={{ paddingLeft: 0, marginBottom: 1 }}>
                        Volver a Reportes
                    </Button>
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Horarios Disponibles
                    </Typography>
                </Grid>

                {/* Filtros de la consulta. */}
                <Grid item xs={12} sm={12}>
                    <Card>
                        <CardContent sx={{ padding: "10px 10px 10px 10px !important" }}>
                            <Grid container spacing={1} alignItems={"center"}>
                                <Grid item xs={12} sm={3}>
                                    <FiltroFilial
                                        filiales={filiales}
                                        idFilial={idFilial}
                                        bloqueado={bloqueado}
                                        onChange={(id) => {
                                            setIdFilial(id);
                                            setTerapeutaSeleccionado(0);
                                            setDisponibilidad([]);
                                            setFiltrado(false);
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={3}>
                                    <FormControl fullWidth>
                                        <InputLabel htmlFor="input-terapeuta" shrink>Terapeuta</InputLabel>
                                        <Select
                                            native
                                            label="Terapeuta"
                                            value={terapeutaSeleccionado || 0}
                                            onChange={(event) => setTerapeutaSeleccionado(parseInt(event.target.value, 10))}
                                            inputProps={{ name: 'idTerapeuta', id: 'input-terapeuta' }}
                                            size="small"
                                        >
                                            <option value={0}>{"(Seleccione)"}</option>
                                            {opcionesTerapeuta.map((row) => (
                                                <option key={row.id} value={row.id}>{row.descripcion}</option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={2}>
                                    <FormControl fullWidth>
                                        <InputLabel htmlFor="input-turno" shrink>Turno</InputLabel>
                                        <Select
                                            native
                                            label="Turno"
                                            value={turno}
                                            onChange={(event) => setTurno(parseInt(event.target.value, 10))}
                                            inputProps={{ name: 'turno', id: 'input-turno' }}
                                            size="small"
                                        >
                                            {TURNOS.map((row) => (
                                                <option key={row.id} value={row.id}>{row.descripcion}</option>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6} sm={2}>
                                    <Button variant={"contained"} startIcon={<IconSearch />} fullWidth disabled={cargando}
                                        onClick={filtrar}>
                                        {"Filtrar"}
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Disponibilidad por dia y hora. */}
                <Grid item xs={12} sm={12}>
                    <Card>
                        <CardContent sx={{ padding: "10px 10px 10px 10px !important" }}>
                            <Box sx={{ fontWeight: 'bold' }}>
                                <Typography variant="h7">Disponibilidad</Typography>
                            </Box>

                            <TableContainer component={Paper} variant="outlined" sx={{ marginTop: "12px" }}>
                                <Table size="small" sx={{ borderCollapse: "collapse" }}>
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: "#1976d2" }}>
                                            {/* La primera columna lleva los mismos colores que las cabeceras. */}
                                            <TableCell sx={{ color: "white", backgroundColor: "#1976d2", borderRight: SEPARADOR }}>Hora</TableCell>
                                            {DIAS_SEMANA.map((dia) => (
                                                // La cabecera lleva el dia y, debajo, el total de
                                                // tiempo libre de ese dia de la semana. Antes de
                                                // filtrar el total va en cero.
                                                <TableCell key={dia} align="center"
                                                    sx={{
                                                        color: "white",
                                                        backgroundColor: "#1976d2",
                                                        borderRight: SEPARADOR,
                                                    }}>
                                                    <Typography variant="body2" sx={{ color: "white", fontWeight: "bold" }}>
                                                        {dia}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: "white", opacity: "0.9" }}>
                                                        {libresDelDia(dia)}
                                                    </Typography>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {horasVisibles().map((hora) => (
                                            <TableRow key={hora}>
                                                <TableCell sx={{
                                                    fontWeight: "bold",
                                                    color: "white",
                                                    backgroundColor: "#1976d2",
                                                    borderRight: SEPARADOR,
                                                }}>
                                                    {String(hora).padStart(2, "0")}:00
                                                </TableCell>
                                                {DIAS_SEMANA.map((dia) => {
                                                    const celda = celdaDe(dia, hora);

                                                    return (
                                                        <TableCell key={dia} align="center"
                                                            sx={{
                                                                padding: 0,
                                                                height: ALTO_BARRA,
                                                                lineHeight: 0,
                                                                verticalAlign: "top",
                                                                borderRight: SEPARADOR,
                                                                borderBottom: SEPARADOR,
                                                            }}>
                                                            {celda ? (
                                                                <BarraMinutos celda={celda} hora={hora} />
                                                            ) : (
                                                                <Box sx={{
                                                                height: "100%",
                                                                backgroundColor: COLOR_FUERA,
                                                            }} />
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                        {!filtrado && horasVisibles().length > 0 && (
                                            <TableRow>
                                                <TableCell colSpan={DIAS_SEMANA.length + 1} align="center">
                                                    {"Elija un terapeuta y pulse Filtrar"}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {filtrado && (
                                <Typography variant="body2" color="textSecondary" sx={{ marginTop: "8px" }}>
                                    {"Cada celda es una barra vertical con los 60 minutos de la hora y los minutos que tiene ocupados: rosado el minuto ocupado, verde el libre y gris claro el que queda fuera del horario de atencion. Al mirar la columna de un dia se ve la franja real que ocupan sus sesiones. La cabecera de cada dia totaliza el tiempo libre de la semana."}
                                    {" " + config.LV_inicio + " a " + config.LV_fin + " de lunes a viernes, y " + config.SA_inicio + " a " + config.SA_fin + " el sabado."}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Snackbar open={openMessage} autoHideDuration={5000} onClose={() => setOpenMessage(!openMessage)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}>
                <Alert onClose={() => setOpenMessage(!openMessage)} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

// El backend devuelve el dia con o sin tilde; se comparan sin ella.
function normalizarDia(dia) {
    const limpio = (dia || "").toString().trim().toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "");

    return DIAS_SEMANA.find((d) => d.toLowerCase() === limpio) || "";
}

export default Index;